import { CommonModule, DOCUMENT } from '@angular/common';

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  computed,
  forwardRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { BaseControlValueAccessorDirective } from '../directives/base-control-value-accessor.directive';
import { AutocompleteOption } from '../models/autocomplete-option.model';

@Component({
  selector: 'lib-autocomplete',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatInputModule,
  ],
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteComponent
  extends BaseControlValueAccessorDirective<string>
  implements OnDestroy
{
  private readonly document = inject(DOCUMENT);
  private readonly ngZone = inject(NgZone);

  private readonly inputElement =
    viewChild<ElementRef<HTMLInputElement>>('input');
  private readonly trigger = viewChild(MatAutocompleteTrigger);

  /*
   * The text currently displayed in the input.
   * This is intentionally separate from the CVA value.
   *
   * Example:
   * value() = "MAEU"
   * inputText() = "Maersk Line"
   */
  private readonly internalInputText = signal('');

  /*
   * True only while the user is entering filter text.
   * When false, opening the panel displays the complete options list.
   */
  private readonly userIsTyping = signal(false);

  private blurValidationPending = false;
  private mouseSelectionInProgress = false;

  protected readonly clearInputLabel = 'Clear input';
  protected readonly toggleOptionsLabel = 'Toggle options';

  public readonly placeholder = input<string>('');
  public readonly options = input<AutocompleteOption[]>([]);
  public readonly loading = input<boolean>(false);
  public readonly filterStartsWith = input<boolean>(false);
  public readonly strictSelection = input<boolean>(true);

  public readonly widthPx = input<number | null>(null);
  public readonly heightPx = input<number | null>(null);

  public readonly appearance = input<'default' | 'form'>('default');
  public readonly hasError = input<boolean>(false);
  public readonly readonly = input<boolean>(false);
  public readonly clearable = input<boolean>(true);

  /*
   * Emitted whenever the options panel is opened.
   * The parent decides whether an API call is required.
   */
  public readonly optionsRequested = output<void>();

  public readonly optionSelected = output<AutocompleteOption>();
  public readonly cleared = output<void>();

  public isPanelOpen = false;
  public panelWidth = 0;

  public readonly inputText = computed(() => {
    if (this.userIsTyping()) {
      return this.internalInputText();
    }

    return this.resolveDisplayText(this.value(), this.options());
  });

  /*
   * While the user is not typing, display the complete list.
   *
   * This ensures that opening an autocomplete with an existing selected value
   * does not filter the list down to only the selected option.
   */
  public readonly filteredOptions = computed(() => {
    const options = this.options();

    if (!this.userIsTyping()) {
      return [...options];
    }

    const filterValue = this.inputText().trim().toLowerCase();

    if (!filterValue) {
      return [...options];
    }

    return options.filter((option) => {
      const optionValue = option.value.trim().toLowerCase();
      const displayValue = option.displayValue?.trim().toLowerCase() ?? '';

      if (this.filterStartsWith()) {
        return (
          optionValue.startsWith(filterValue) ||
          displayValue.startsWith(filterValue)
        );
      }

      return (
        optionValue.includes(filterValue) || displayValue.includes(filterValue)
      );
    });
  });

  public onInput(event: Event): void {
    if (this.disabled()) {
      return;
    }

    const inputText = (event.target as HTMLInputElement).value;

    this.userIsTyping.set(true);
    this.internalInputText.set(inputText);

    /*
     * Free text is immediately written to the form only when strict
     * selection is disabled.
     */
    if (!this.strictSelection()) {
      this.updateValue(inputText);
    }
  }

  public onFocus(event: FocusEvent): void {
    if (this.disabled()) {
      return;
    }

    const element = event.target as HTMLInputElement;

    this.panelWidth = element.offsetWidth;
    this.openPanel();
  }

  public onEnter(): void {
    if (this.disabled() || !this.strictSelection()) {
      return;
    }

    queueMicrotask(() => {
      if (!this.userIsTyping()) {
        return;
      }

      this.blurValidationPending = false;
      this.commitTypedValue();
      this.closePanel();
    });
  }

  public onBlur(): void {
    this.markAsTouched();

    if (!this.strictSelection()) {
      this.userIsTyping.set(false);
      return;
    }

    this.blurValidationPending = true;

    queueMicrotask(() => {
      if (!this.blurValidationPending) {
        return;
      }

      this.commitTypedValue();
      this.blurValidationPending = false;
    });
  }

  public onPanelOpen(): void {
    this.isPanelOpen = true;
    this.addScrollListener();
  }

  public onPanelClose(): void {
    this.isPanelOpen = false;
    this.removeScrollListener();
  }

  private readonly onDocumentScroll = (event: Event): void => {
    const target = event.target;
    const panel = this.trigger()?.autocomplete?.panel?.nativeElement;

    // Allow the autocomplete options panel itself to scroll.
    if (
      target instanceof Node &&
      (panel === target || panel?.contains(target))
    ) {
      return;
    }

    this.ngZone.run(() => {
      this.closePanel();
    });
  };

  private addScrollListener(): void {
    // Defensively prevent duplicate registration.
    this.removeScrollListener();

    this.ngZone.runOutsideAngular(() => {
      this.document.addEventListener('scroll', this.onDocumentScroll, {
        capture: true,
        passive: true,
      });
    });
  }

  private removeScrollListener(): void {
    this.document.removeEventListener('scroll', this.onDocumentScroll, {
      capture: true,
    });
  }

  public ngOnDestroy(): void {
    this.removeScrollListener();
  }

  public togglePanel(): void {
    if (this.disabled() || !this.trigger) {
      return;
    }

    if (this.trigger()?.panelOpen) {
      this.trigger()?.closePanel();
      return;
    }

    this.panelWidth = this.inputElement()?.nativeElement.offsetWidth ?? 0;

    this.openPanel();
    this.focus();
  }

  private commitOption(option: AutocompleteOption): void {
    this.blurValidationPending = false;

    this.updateValue(option.value);
    this.userIsTyping.set(false);
    this.markAsTouched();

    this.optionSelected.emit(option);
  }

  public selectOptionByMouse(
    option: AutocompleteOption,
    event: MouseEvent
  ): void {
    event.preventDefault();
    event.stopPropagation();

    this.mouseSelectionInProgress = true;
    this.commitOption(option);

    queueMicrotask(() => {
      this.mouseSelectionInProgress = false;
    });
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    if (this.mouseSelectionInProgress) {
      return;
    }

    const option = event.option.value as AutocompleteOption;

    this.commitOption(option);
  }

  public clearInput(event: Event): void {
    event.stopPropagation();

    if (this.disabled()) {
      return;
    }

    this.blurValidationPending = false;
    this.userIsTyping.set(false);
    this.internalInputText.set('');

    this.trigger()?.autocomplete?.options?.forEach((option) => {
      option.deselect(false);
    });

    /*
     * Use an empty string because the form may uses
     * NonNullableFormBuilder.
     */
    this.updateValue('');
    this.markAsTouched();
    this.cleared.emit();

    queueMicrotask(() => {
      this.focus();
    });
  }

  public focus(): void {
    this.inputElement()?.nativeElement.focus();
  }

  public closePanel(): void {
    this.trigger()?.closePanel();
  }

  private openPanel(): void {
    if (this.disabled() || !this.trigger) {
      return;
    }

    if (this.trigger()?.panelOpen) {
      return;
    }

    /*
     * Request options before opening the panel.
     *
     * This is important when the initial options list is empty.
     * Angular Material may not emit "opened" if there are no options.
     */
    this.optionsRequested.emit();

    this.trigger()?.openPanel();
  }

  private commitTypedValue(): void {
    const currentText = this.inputText().trim();

    if (!currentText) {
      this.userIsTyping.set(false);
      this.updateValue('');
      return;
    }

    const matchedOption = this.findMatchingOption(currentText);

    if (!matchedOption) {
      this.userIsTyping.set(false);
      this.updateValue('');
      return;
    }

    this.updateValue(matchedOption.value);
    this.userIsTyping.set(false);
  }

  private resolveDisplayText(
    value: string | null,
    options: readonly AutocompleteOption[]
  ): string {
    if (!value) {
      return '';
    }

    const normalizedValue = value.trim().toLowerCase();

    const selectedOption = options.find(
      (option) => option.value.trim().toLowerCase() === normalizedValue
    );

    /*
     * If options have not loaded, display the stored value temporarily.
     * Once options arrive, the computed inputText signal automatically
     * resolves and displays the corresponding displayValue.
     */
    return selectedOption?.displayValue ?? selectedOption?.value ?? value;
  }

  private findMatchingOption(text: string): AutocompleteOption | undefined {
    const normalizedText = text.trim().toLowerCase();

    return this.options().find((option) => {
      const normalizedValue = option.value.trim().toLowerCase();

      const normalizedDisplayValue = option.displayValue?.trim().toLowerCase();

      return (
        normalizedValue === normalizedText ||
        normalizedDisplayValue === normalizedText
      );
    });
  }

  public displayOption(option: AutocompleteOption | string | null): string {
    if (!option) {
      return '';
    }

    if (typeof option === 'string') {
      return option;
    }

    return option.displayValue ?? option.value;
  }

  public isOptionSelected(option: AutocompleteOption): boolean {
    const selectedValue = this.value();

    if (!selectedValue) {
      return false;
    }

    return (
      option.value.trim().toLowerCase() === selectedValue.trim().toLowerCase()
    );
  }
}
